import { Capacitor } from '@capacitor/core';

/**
 * Get a phone onto the build that was just deployed.
 *
 * `index.html` is served `no-cache`, so a fresh page load always gets the
 * current build. The trouble is that a phone rarely does a fresh page load: a
 * mobile browser keeps the tab alive in the app switcher (or in the
 * back-forward cache) for days, and there is no Ctrl+Shift+R to press. The JS
 * running in that tab is whatever was deployed when it was opened.
 *
 * So the build writes `version.json` beside `index.html` (a Vite plugin in
 * `vite.config.ts`), and this compares it against the `__APP_VERSION__` baked
 * into the running bundle:
 *
 * - **On boot**, a mismatch reloads at once. Nothing has been typed yet; this
 *   catches an `index.html` a browser held on to anyway.
 * - **On returning to the tab, and every 10 minutes while it is showing**, a
 *   mismatch only marks an update as waiting. A reload there could throw away
 *   a half-filled form, so `UpdateAvailableBanner` offers it instead, and the
 *   next page navigation takes it (`applyPendingUpdate`).
 * - **Pull to refresh** reloads when an update is waiting: it is the phone's
 *   refresh gesture, and the person asked for it.
 *
 * A reload that does not cure the mismatch (a CDN edge still serving the old
 * `index.html`) is not repeated for that version, so it can never loop.
 *
 * Native is skipped: the app's bundle comes from the store build or the live
 * update plugin (`services/liveUpdate.ts`), not from this server.
 */

const POLL_MS = 10 * 60_000;
/** Do not ask again within this long of the last check (focus + visibility fire together). */
const MIN_GAP_MS = 30_000;
const RELOADED_FOR_KEY = 'versionCheckReloadedFor';

type Listener = (pending: boolean) => void;

let pendingVersion: string | null = null;
let lastCheckAt = 0;
let inFlight: Promise<string | null> | null = null;
const listeners = new Set<Listener>();

function enabled(): boolean {
  return !import.meta.env.DEV && !Capacitor.isNativePlatform();
}

async function fetchDeployedVersion(): Promise<string | null> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      // The query string gives the CDN a key it has never cached, and no-store
      // keeps the browser from answering it from its own cache.
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return null;
      // The load balancer answers a missing file with index.html and a 200.
      if (!(res.headers.get('content-type') ?? '').includes('json')) return null;
      const body = (await res.json()) as { version?: unknown };
      return typeof body.version === 'string' ? body.version : null;
    } catch {
      return null;
    } finally {
      lastCheckAt = Date.now();
      inFlight = null;
    }
  })();
  return inFlight;
}

function alreadyReloadedFor(version: string): boolean {
  try {
    return sessionStorage.getItem(RELOADED_FOR_KEY) === version;
  } catch {
    // No storage, no loop guard: never reload automatically.
    return true;
  }
}

function reloadFor(version: string): boolean {
  if (alreadyReloadedFor(version)) return false;
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, version);
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

function setPending(version: string | null): void {
  if (pendingVersion === version) return;
  pendingVersion = version;
  listeners.forEach((l) => l(version !== null));
}

/** Ask the server which build is deployed; marks an update as waiting if it differs. */
export async function checkForNewVersion(force = false): Promise<boolean> {
  if (!enabled()) return false;
  if (!force && Date.now() - lastCheckAt < MIN_GAP_MS) return pendingVersion !== null;
  const deployed = await fetchDeployedVersion();
  if (deployed && deployed !== __APP_VERSION__) setPending(deployed);
  return pendingVersion !== null;
}

/** Reload now if an update is waiting. Returns true when the page is reloading. */
export function applyPendingUpdate(): boolean {
  return pendingVersion !== null && reloadFor(pendingVersion);
}

/** Reload to the waiting update, at the person's request. Ignores the loop guard. */
export function reloadToNewVersion(): void {
  try {
    if (pendingVersion) sessionStorage.setItem(RELOADED_FOR_KEY, pendingVersion);
  } catch {
    // Reloading anyway: they pressed the button.
  }
  window.location.reload();
}

export function subscribeToUpdate(listener: Listener): () => void {
  listeners.add(listener);
  listener(pendingVersion !== null);
  return () => listeners.delete(listener);
}

/** Call once, before render. */
export function initVersionCheck(): void {
  if (!enabled()) return;

  // Boot: reload straight away on a stale page.
  void fetchDeployedVersion().then((deployed) => {
    if (!deployed || deployed === __APP_VERSION__) return;
    if (!reloadFor(deployed)) setPending(deployed);
  });

  const onResume = () => {
    if (document.visibilityState === 'visible') void checkForNewVersion();
  };
  document.addEventListener('visibilitychange', onResume);
  window.addEventListener('focus', onResume);
  // A page restored from the back-forward cache runs no boot code at all.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) void checkForNewVersion(true);
  });
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void checkForNewVersion();
  }, POLL_MS);
}
